import { z } from '@faceup/validation'
import { YES_NO_OPTION_COUNT } from '../constants'
import { FormItemType } from '../enums'
import { LONG_STRING_MAX_LENGTH, STRING_MIN_LENGTH } from '../validationConfig'
import {
  dateFormItemSchema,
  emailFormItemSchema,
  formItemOptionSchema,
  multilineTextFormItemSchema,
  multiSelectFormItemSchema,
  numberFormItemSchema,
  phoneNumberFormItemSchema,
  selectFormItemSchema,
  simpleTextFormItemSchema,
  yesNoFormItemSchema,
} from './abstractFormItemSchemas'
import { longStringSchema } from './commonSchema'

/**
 * Input schemas for the form items of a disclosure form, the write counterpart of
 * `publicDisclosureFormItemSchema`. Shared so the administration can validate the same shapes
 * the endpoints accept.
 *
 * Both schemas are discriminated on `type`, which is what makes the settings of a type - the
 * maximum length, the options, the response limits - validated per type: the variants are
 * strict, so a setting belonging to another type is rejected rather than stripped. The
 * discriminated union doubles as the disclosure form item whitelist (see
 * `allowedDisclosureFormItemTypes`) — types outside it fail validation.
 *
 * On an update the type is a discriminator only. A form item's type is fixed at creation, so the
 * endpoint verifies it against the stored one and refuses a mismatch; nothing reads it to decide
 * what to write.
 *
 * Ids are branded UUIDs on the backend; they cross the wire unbranded and each endpoint brands
 * them at its own boundary.
 */
const buildItemSchema = <TOption extends z.ZodTypeAny>(optionSchema: TOption) => {
  const baseItemFields = {
    label: longStringSchema,
    hint: longStringSchema.nullish(),
    isRequired: z.boolean(),
  }

  const optionsItemFields = {
    ...baseItemFields,
    hasOtherOption: z.boolean(),
    options: z.array(optionSchema).min(1),
  }

  const responseLimitSchema = z.number().int().positive().nullish()

  return z.discriminatedUnion('type', [
    z.strictObject({ type: z.literal(FormItemType.SimpleText), ...baseItemFields }),
    z.strictObject({ type: z.literal(FormItemType.Date), ...baseItemFields }),
    z.strictObject({ type: z.literal(FormItemType.Number), ...baseItemFields }),
    z.strictObject({ type: z.literal(FormItemType.Email), ...baseItemFields }),
    z.strictObject({ type: z.literal(FormItemType.PhoneNumber), ...baseItemFields }),
    z.strictObject({
      type: z.literal(FormItemType.MultilineText),
      ...baseItemFields,
      maxLength: z.number().int().min(STRING_MIN_LENGTH).max(LONG_STRING_MAX_LENGTH).nullish(),
    }),
    z.strictObject({ type: z.literal(FormItemType.Select), ...optionsItemFields }),
    z.strictObject({
      type: z.literal(FormItemType.YesNo),
      ...baseItemFields,
      options: z.array(optionSchema).length(YES_NO_OPTION_COUNT),
    }),
    z.strictObject({
      type: z.literal(FormItemType.MultiSelect),
      ...optionsItemFields,
      minResponses: responseLimitSchema,
      maxResponses: responseLimitSchema,
    }),
  ])
}

/**
 * `isAutoApproved` marks an answer that auto-approves the submission. Left out, an existing option
 * keeps the marking it has, so an edit that only renames options keeps their rules (spec AC-007),
 * and a new option is not marked.
 */
export const createDisclosureFormItemSchema = buildItemSchema(
  z.strictObject({ label: longStringSchema, isAutoApproved: z.boolean().optional() })
)

/**
 * The new state is the item's whole definition, not just the fields that differ from the stored
 * one: options carry the id of the option they edit, new options come without one, and options
 * left out of the list are deleted. The list order becomes the option order.
 */
export const updateDisclosureFormItemSchema = buildItemSchema(
  z.strictObject({
    id: z.uuidv7().nullish(),
    label: longStringSchema,
    isAutoApproved: z.boolean().optional(),
  })
)

const optionWithAutoApprovalSchema = formItemOptionSchema.extend({ isAutoApproved: z.boolean() })

/**
 * A form item as the administration edits it: the public shape, plus which options auto-approve.
 * Never sent to respondents, who must not learn which answers auto-approve.
 */
export const disclosureFormItemWithAutoApprovalSchema = z.discriminatedUnion('type', [
  simpleTextFormItemSchema,
  dateFormItemSchema,
  numberFormItemSchema,
  emailFormItemSchema,
  phoneNumberFormItemSchema,
  multilineTextFormItemSchema,
  selectFormItemSchema.extend({ options: z.array(optionWithAutoApprovalSchema) }),
  multiSelectFormItemSchema.extend({ options: z.array(optionWithAutoApprovalSchema) }),
  yesNoFormItemSchema.extend({
    options: z.array(optionWithAutoApprovalSchema).length(YES_NO_OPTION_COUNT),
  }),
])

export type DisclosureFormItemWithAutoApproval = z.infer<
  typeof disclosureFormItemWithAutoApprovalSchema
>
